import React, { useState } from 'react';
import styled from 'styled-components';
import Button from 'src/components/form/Button';
import MaterialInput from 'src/components/form/MaterialInput';
import { useFormik } from 'formik';
import { queryCache } from 'react-query';
import { QKeys } from 'src/api/types';
import * as Yup from 'yup';
import { Poppins, Spacer } from 'src/common';
import { Guard } from './Guard';
import { Modal } from './comps';
import { getDiff } from 'src/utils/getDiff';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { useCache } from 'src/graphql/useCache';
import { useUpsertAssessmentMutation } from 'src/graphql/_generated';

const StyledModal = styled(Modal)`
  width: 566px;
  color: ${({ theme }) => theme.colors.cflowerBlue};

  .error {
    color: ${({ theme }) => theme.colors.error};
    text-align: center;
  }
`;

const assessmentSchema = Yup.object({
  executiveSummary: Yup.string().nullable(),
});

interface ExecSummaryModalProps {
  assessmentId: string;
  executiveSummary?: string | null;
  onClose: () => void;
}

export const ExecSummaryModal: React.FC<ExecSummaryModalProps> = ({ assessmentId, onClose, executiveSummary }) => {
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const [err, setErr] = useState('');
  const { refetchQuery } = useCache();

  const [upsert] = useUpsertAssessmentMutation({
    onCompleted: () => {
      queryCache.invalidateQueries([QKeys.Audit]);
      refetchQuery({ include: ['assessment'] });
      onClose();
    },
    onError: (err: any) => setErr(err.messahe || 'Something went wrong'),
  });

  const { values, dirty, handleChange, handleBlur, touched, errors, isValid, handleSubmit, isSubmitting } = useFormik({
    initialValues: {
      executiveSummary,
    },
    validationSchema: assessmentSchema,
    onSubmit: async (values) => {
      setErr('');
      const diff = getDiff({ executiveSummary }, values);
      return upsert({ variables: { assessmentData: { ...diff, id: assessmentId } } });
    },
    enableReinitialize: true,
  });

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={() => onClose()}
      isOpen={isCloseRequested}
      isDirty={dirty}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen>
          <Poppins className="m-title" px={28}>
            Executive Summary
          </Poppins>
          <Spacer $px={30} />
          <MaterialInput
            name="executiveSummary"
            value={values.executiveSummary}
            onChange={handleChange}
            onBlur={handleBlur}
            multiline
            fullWidth
            error={touched.executiveSummary && Boolean(errors.executiveSummary)}
            helperText={touched.executiveSummary && errors.executiveSummary}
            placeholder="Executive summary"
            rows={10}
            data-cy="exec-summary-input"
          />
          <Spacer $px={30} />
          <Button
            disabled={!isValid || isSubmitting || !dirty}
            onClick={() => {
              handleSubmit();
              mpEvent(MPEvents.ButtonClick, {
                button: 'Add',
                modal: 'Executive summary modal',
              });
            }}
            primary
            css="width: 100%;"
            data-cy="submit"
          >
            ADD
          </Button>
          {err && (
            <>
              <Spacer $px={20} />
              <Poppins px={14} className="error">
                {err}
              </Poppins>
            </>
          )}
        </StyledModal>
      }
    />
  );
};
